import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, currency } from "@/lib/api";
import { useLead } from "@/context/LeadDialog";
import { Star, ShieldCheck, Truck, Award, ArrowRight } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const { openLead } = useLead();

  useEffect(() => {
    setImg(0);
    api.get(`/products/${slug}`).then(({ data }) => setP(data)).catch(() => setP(null));
  }, [slug]);

  if (!p) return <div className="py-24 text-center text-asure-text2">Loading…</div>;

  const off = p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 pt-10 pb-32 md:pb-24">
      <nav className="text-xs text-asure-text2 mb-6"><Link to="/shop" className="hover:text-asure-forest">Shop</Link> · <span className="text-asure-forest">{p.name}</span></nav>
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <div className="rounded-3xl overflow-hidden bg-white border border-[color:var(--asure-border)]">
            <img src={p.images[img]} alt={p.name} className="w-full aspect-square object-cover" />
          </div>
          <div className="mt-4 grid grid-cols-5 gap-3">
            {p.images.map((src, i) => (
              <button key={i} onClick={() => setImg(i)} className={`rounded-lg overflow-hidden border-2 transition-colors ${i === img ? 'border-asure-forest' : 'border-transparent'}`} data-testid={`thumb-${i}`}>
                <img src={src} alt="" className="w-full aspect-square object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="eyebrow">{p.category.replace(/-/g, ' ')}</div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight mt-3">{p.name}</h1>
          {p.form_factor && <div className="mt-2 text-sm text-asure-text2">{p.form_factor}</div>}
          <div className="mt-3 flex items-center gap-3 text-sm text-asure-text2 flex-wrap">
            {p.is_hero && <span className="px-2 py-0.5 bg-asure-forest text-white text-[10px] rounded-full font-bold tracking-wider">HERO PRODUCT</span>}
            {p.featured && !p.is_hero && <span className="px-2 py-0.5 bg-asure-orange text-white text-[10px] rounded-full font-bold tracking-wider">FEATURED</span>}
          </div>

          <p className="mt-6 text-asure-text2 leading-relaxed">{p.description}</p>

          <div className="mt-6 flex items-baseline gap-3 flex-wrap">
            <span className="text-4xl font-semibold text-asure-forest font-display">{currency(p.price)}</span>
            {p.mrp > p.price && <>
              <span className="text-lg line-through text-asure-text2/60">{currency(p.mrp)}</span>
              <span className="text-sm font-bold text-asure-orange">{off}% introductory off</span>
            </>}
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {p.highlights?.map((h) => <span key={h} className="text-[11px] font-semibold tracking-wide px-3 py-1 rounded-full border border-asure-forest/30 text-asure-forest">{h}</span>)}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => openLead(p.name)} className="btn-primary" data-testid="pd-enquire">I'm Interested <ArrowRight size={16} /></button>
            <a href="tel:+918269784515" className="btn-ghost" data-testid="pd-call">Call to Order</a>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-4">
            <FeatureLine icon={Truck} label="Pan-India delivery" />
            <FeatureLine icon={ShieldCheck} label="Quality tested" />
            <FeatureLine icon={Award} label="Premium quality" />
          </div>

          <div className="mt-6 text-[11px] text-asure-text2 leading-relaxed">
            Nutraceutical · Not for medicinal use. This product is not intended to diagnose, treat, cure or prevent any disease.
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-16">
        <Tabs defaultValue="overview">
          <TabsList className="flex-wrap h-auto p-1">
            {[["overview","Overview"],["ingredients","Ingredients"],["how","How to Use"],["quality","Quality"],["faq","FAQs"]].map(([v,l]) => <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}>{l}</TabsTrigger>)}
          </TabsList>
          <TabsContent value="overview" className="pt-8 text-asure-text2 leading-relaxed">{p.description}</TabsContent>
          <TabsContent value="ingredients" className="pt-8 text-asure-text2">
            <ul className="list-disc pl-6">{p.ingredients?.map((i) => <li key={i}>{i}</li>)}</ul>
          </TabsContent>
          <TabsContent value="how" className="pt-8 text-asure-text2">Take as directed on the pack, ideally as part of a consistent daily routine. Consult a healthcare professional if pregnant, nursing, or on medication.</TabsContent>
          <TabsContent value="quality" className="pt-8 text-asure-text2">Every batch subject to quality checks in line with applicable Indian nutraceutical guidelines. Batch information available on-pack. [FSSAI LICENSE NUMBER]</TabsContent>
          <TabsContent value="faq" className="pt-8 text-asure-text2">See our <Link to="/faq" className="underline">full FAQ page</Link> for common questions.</TabsContent>
        </Tabs>
      </div>

      {/* sticky mobile CTA */}
      <div className="fixed bottom-[60px] inset-x-0 lg:hidden bg-white border-t border-[color:var(--asure-border)] p-3 z-30 mobile-bottom-nav">
        <div className="max-w-7xl mx-auto flex items-center gap-3">
          <div className="flex-1">
            <div className="text-xs text-asure-text2">{p.name.split('—')[0]}</div>
            <div className="text-lg font-semibold text-asure-forest">{currency(p.price)}</div>
          </div>
          <button onClick={() => openLead(p.name)} className="btn-primary flex-1" data-testid="sticky-enquire">I'm Interested</button>
        </div>
      </div>
    </div>
  );
}

function FeatureLine({ icon: I, label }) {
  return <div className="flex items-center gap-2 text-xs text-asure-text2"><I size={16} className="text-asure-forest" />{label}</div>;
}
